<script setup lang="ts">
/**
 * One node of a payload tree. Recursive: it renders itself for children.
 *
 * Objects and arrays past `openDepth` start collapsed, because a tool result can be thousands of
 * lines and the point of this view is to let someone find one field, not to scroll.
 */
import { computed, ref } from 'vue'
import { childCount, type JsonNode } from '../lib/json'

const props = withDefaults(defineProps<{ node: JsonNode; name?: string; depth?: number; openDepth?: number }>(), {
    depth: 0,
    openDepth: 2,
})

const open = ref(props.depth < props.openDepth)
const branch = computed(
    () => props.node.kind === 'object' || props.node.kind === 'array' || props.node.kind === 'embedded',
)
const count = computed(() => childCount(props.node))

/** The children to render under this node, flattening the decode wrapper away. */
const inner = computed(() => (props.node.kind === 'embedded' ? props.node.node : props.node))

const entries = computed(() => {
    const node = inner.value
    if (node.kind === 'object') return node.entries
    if (node.kind === 'array') return node.items.map((item, index) => ({ key: String(index), node: item }))
    return null
})

const summary = computed(() => {
    const n = count.value
    if (inner.value.kind === 'array') return `[ ${n} ${n === 1 ? 'item' : 'items'} ]`
    if (inner.value.kind === 'object') return `{ ${n} ${n === 1 ? 'field' : 'fields'} }`
    return '{ decoded }'
})
</script>

<template>
    <div class="zh-json-node">
        <div class="zh-json-row">
            <button
                v-if="branch"
                type="button"
                class="zh-json-toggle"
                :aria-expanded="open"
                :aria-label="open ? `Collapse ${name ?? 'node'}` : `Expand ${name ?? 'node'}`"
                @click="open = !open"
            >
                <span aria-hidden="true">{{ open ? '▾' : '▸' }}</span>
            </button>
            <span v-else class="zh-json-toggle zh-json-toggle--empty" aria-hidden="true" />

            <span v-if="name !== undefined" class="zh-json-key">{{ name }}</span>
            <span v-if="name !== undefined" class="zh-json-punct">:</span>

            <!-- An embedded document is worth calling out: it was a string until we decoded it. -->
            <span v-if="node.kind === 'embedded'" class="zh-json-embedded">JSON</span>

            <template v-if="branch">
                <button v-if="!open" type="button" class="zh-json-summary" @click="open = true">{{ summary }}</button>
            </template>
            <template v-else-if="node.kind === 'primitive'">
                <span class="zh-json-value" :data-type="node.type">{{
                    node.type === 'string' ? `"${node.value}"` : node.value
                }}</span>
            </template>
        </div>

        <div v-if="branch && open" class="zh-json-children">
            <ZhJsonNode
                v-for="entry in entries"
                :key="entry.key"
                :node="entry.node"
                :name="entry.key"
                :depth="depth + 1"
                :open-depth="openDepth"
            />
            <!-- A decoded scalar is rare but possible: "\"just a string\"" round-trips to one. -->
            <ZhJsonNode v-if="!entries" :node="inner" :depth="depth + 1" :open-depth="openDepth" />
        </div>
    </div>
</template>

<style scoped>
.zh-json-row {
    display: flex;
    align-items: baseline;
    gap: 0.375rem;
    line-height: 1.6;
}

.zh-json-children {
    /* The guide line is what makes depth readable without indentation getting expensive. */
    margin-left: 0.4375rem;
    padding-left: 0.625rem;
    border-left: 1px solid color-mix(in srgb, var(--dirty-white) 12%, transparent);
}

.zh-json-toggle {
    flex: none;
    width: 0.875rem;
    color: var(--muted-grey);
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    cursor: pointer;
    line-height: 1.6;
}

.zh-json-toggle:hover {
    color: var(--dirty-white);
}

.zh-json-toggle--empty {
    cursor: default;
}

/*
 * BG §18.3 gives colour a meaning, and inside a payload the meaning is structure rather than run
 * state: cyan is the data label, amber and magenta separate the literal types. Nothing in this
 * view renders a run status, so there is no collision with what those colours mean elsewhere.
 */
.zh-json-key {
    color: var(--electric-cyan);
}

.zh-json-punct {
    color: var(--muted-grey);
}

.zh-json-value {
    color: var(--dirty-white);
    overflow-wrap: anywhere;
}

.zh-json-value[data-type='number'] {
    color: var(--warning-amber);
    font-variant-numeric: tabular-nums;
}

.zh-json-value[data-type='boolean'] {
    color: var(--neon-magenta);
}

.zh-json-value[data-type='null'] {
    color: var(--muted-grey);
}

.zh-json-summary {
    color: var(--muted-grey);
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    cursor: pointer;
}

.zh-json-summary:hover {
    color: var(--electric-cyan);
}

.zh-json-embedded {
    flex: none;
    border: 1px solid color-mix(in srgb, var(--electric-cyan) 40%, transparent);
    padding: 0 0.25rem;
    font-size: 0.625rem;
    letter-spacing: var(--tracking-system);
    color: var(--electric-cyan);
}
</style>
